/**
 * Credential Dialog with Inline Login Test
 *
 * A wrapper component that orchestrates the multi-phase credential flow:
 * 1. User chooses: auto-provision email user OR enter credentials manually
 * 2. User fills/reviews form → saves
 * 3. Credential saves via API
 * 4. Modal expands to show live browser test inline
 * 5. Success → brief success view → auto-close
 * 6. Failure → "Login Failed" view with Edit & Retry or Skip buttons
 */

import type { ApplicationType } from '@chatsdet/types';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogTrigger, DialogContent, Box, Button, Flex, Text } from '@chatsdet/ui';
import {
  EnvelopeSimple,
  IdentificationCard,
  Key,
  Lock,
  PencilSimple,
  PlugsConnected,
  Shield,
} from '@phosphor-icons/react';
import { TEST_LOGIN_DOMAIN } from '../../config';
import { fetchOrgPropertyById } from '../../hooks/useOrgProperty';
import { useAuth } from '../../contexts/AuthContext';
import { CredentialForm } from './CredentialForm';
import { CredentialTestingView } from './credential-dialog/CredentialTestingView';
import {
  useCredentialDialogState,
  type DialogPhase,
} from './credential-dialog/useCredentialDialogState';

type CredentialDialogWithTestProps = {
  propertyId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialCredentialId?: string;
  /** Pre-binds the new credential to a persona (credential template). */
  initialTemplateId?: string | null;
  /** Renders the persona select read-only (create-from-persona flow). */
  lockTemplate?: boolean;
  onSuccess?: (credentialId: string) => void;
  onSkip?: () => void;
};

/**
 * Get dialog size based on phase
 */
function getDialogSize(phase: DialogPhase): 'lg' | '4xl' {
  switch (phase) {
    case 'form':
    case 'saving':
    case 'success':
      return 'lg';
    case 'testing':
    case 'failure':
      return '4xl';
  }
}

/**
 * Get dialog title based on phase and edit mode
 */
function getDialogTitle(phase: DialogPhase, isEditMode: boolean): string | undefined {
  switch (phase) {
    case 'form':
      return isEditMode ? 'Edit Credential' : 'Add Credential';
    case 'saving':
      return 'Saving...';
    case 'testing':
    case 'success':
    case 'failure':
      return undefined; // Title is shown in the testing view
  }
}

export function CredentialDialogWithTest({
  propertyId,
  isOpen,
  onOpenChange,
  initialCredentialId,
  initialTemplateId,
  lockTemplate,
  onSuccess,
  onSkip,
}: CredentialDialogWithTestProps) {
  const isEditMode = !!initialCredentialId;

  // Same query key as the property-detail pages, so this normally reads from cache.
  const propertyQ = useQuery({
    queryKey: ['property', propertyId],
    queryFn: async () => {
      return fetchOrgPropertyById<{ id: string; type: ApplicationType }>(propertyId);
    },
  });
  const isMcpProperty = propertyQ.data?.type === 'mcp';

  const state = useCredentialDialogState({
    propertyId,
    initialCredentialId,
    initialTemplateId,
    onSuccess,
    onSkip,
    onClose: () => onOpenChange(false),
  });

  const {
    phase,
    credentialMode,
    credentialId,
    formData,
    setFormData,
    browserStream,
    isLoading,
    isProvisioning,
    errorMessage,
    handleSave,
    handleSaveOnly,
    handleRetry,
    handleSkip,
    handleCancel,
    handleCancelTest,
    handleContinue,
    provisionEmailUser,
    chooseManualEntry,
    chooseApiKey,
    chooseApiBasic,
    chooseApiLogin,
    chooseGoogleOauth,
    chooseTestSignIn,
    isFormValid,
  } = state;

  const { user } = useAuth();
  const testSignInEnabled = Boolean(user?.features?.testSignIn);

  // MCP servers authenticate with a bearer token, so a new credential defaults
  // straight to the api_key provider instead of the browser-login chooser.
  React.useEffect(() => {
    if (isMcpProperty && !isEditMode && credentialMode === 'choosing') {
      chooseApiKey();
    }
  }, [isMcpProperty, isEditMode, credentialMode, chooseApiKey]);

  // Handle dialog close
  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        handleCancel();
      }
      onOpenChange(open);
    },
    [handleCancel, onOpenChange]
  );

  const dialogSize = getDialogSize(phase);
  const dialogTitle = getDialogTitle(phase, isEditMode);
  const showsBrowserTelemetry = phase === 'testing' || phase === 'failure';

  // Show the chooser when: new credential and user hasn't chosen yet
  const showChooser = !isEditMode && credentialMode === 'choosing';
  // Every mode except the chooser itself renders the form. Derived rather than
  // enumerated on purpose: the enumerated form silently omitted `google_oauth`,
  // so picking it left both this and `showChooser` false and the dialog rendered
  // an empty body with no footer. A new CredentialMode must not be able to fall
  // through both branches.
  const showForm = isEditMode || credentialMode !== 'choosing';

  // Footer lives in the Dialog's pinned (sticky) footer bar. Only the form phase
  // shows action buttons; the testing/success/failure phases drive their own
  // actions from inside CredentialTestingView.
  // Providers with no login flow to exercise get a single Save button. For
  // `google_oauth` that is not cosmetic: its session is minted server-side from
  // a refresh token, and the API refuses the login test outright with
  // MINTED_SESSION_HAS_NO_LOGIN_FLOW, so a "Save & Test" primary would fail
  // every time it was pressed.
  const isSaveOnlyProvider =
    formData.authProvider === 'api_key' ||
    formData.authProvider === 'api_basic' ||
    formData.authProvider === 'google_oauth';
  const footerNode =
    phase === 'form' && showForm ? (
      <>
        <span className="cred-foot-note">
          <Lock size={13} weight="bold" />
          Encrypted at rest
        </span>
        <Button variant="ghost" onPress={handleCancel} isDisabled={isLoading}>
          Cancel
        </Button>
        {isSaveOnlyProvider ? (
          <Button variant="primary" onPress={handleSaveOnly} isDisabled={!isFormValid || isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onPress={handleSaveOnly}
              isDisabled={!isFormValid || isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="primary" onPress={handleSave} isDisabled={!isFormValid || isLoading}>
              {isLoading ? 'Saving...' : 'Save & Test'}
            </Button>
          </>
        )}
      </>
    ) : phase === 'form' && showChooser ? (
      <Button variant="ghost" onPress={handleCancel} isDisabled={isProvisioning}>
        Cancel
      </Button>
    ) : undefined;

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      size={dialogSize}
      className={
        showsBrowserTelemetry
          ? 'credential-dialog credential-dialog--browser-test'
          : 'credential-dialog'
      }
    >
      <span />
      <Dialog
        title={dialogTitle}
        eyebrow={phase === 'form' ? (isEditMode ? 'Edit credential' : 'New credential') : undefined}
        description={
          phase === 'form'
            ? 'Stored encrypted and used to sign your workflow into the target app.'
            : undefined
        }
        footer={footerNode}
      >
        <AnimatePresence mode="wait">
          {phase === 'form' && (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <DialogContent>
                {showChooser && (
                  <Flex direction="column" gap="md">
                    {!!TEST_LOGIN_DOMAIN && (
                      <Box
                        className="p-4 bg-card border border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                        onClick={provisionEmailUser}
                      >
                        <Flex align="center" gap="sm" className="mb-1.5">
                          <EnvelopeSimple size={20} className="text-primary" />
                          <Text size="sm" weight="semibold">
                            {isProvisioning ? 'Generating...' : 'Provision email user'}
                          </Text>
                        </Flex>
                        <Text size="xs" className="text-muted-foreground">
                          Auto-generate a test account with a unique email address and inbox for
                          receiving verification codes
                        </Text>
                      </Box>
                    )}

                    <Box
                      className="p-4 bg-card border border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                      onClick={chooseManualEntry}
                    >
                      <Flex align="center" gap="sm" className="mb-1.5">
                        <PencilSimple size={20} className="text-muted-foreground" />
                        <Text size="sm" weight="semibold">
                          Enter your own credentials
                        </Text>
                      </Flex>
                      <Text size="xs" className="text-muted-foreground">
                        Manually provide username, password, and login details for an existing
                        account
                      </Text>
                    </Box>

                    <Box
                      className="p-4 bg-card border border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                      onClick={chooseGoogleOauth}
                    >
                      <Flex align="center" gap="sm" className="mb-1.5">
                        <Shield size={20} className="text-muted-foreground" />
                        <Text size="sm" weight="semibold">
                          Sign in with Google (token)
                        </Text>
                      </Flex>
                      <Text size="xs" className="text-muted-foreground">
                        For apps behind Google SSO. Uses a refresh token to mint a session directly
                        — no login screen for the agent to drive.
                      </Text>
                    </Box>

                    <Box
                      className="p-4 bg-card border border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                      onClick={chooseApiKey}
                    >
                      <Flex align="center" gap="sm" className="mb-1.5">
                        <Key size={20} className="text-muted-foreground" />
                        <Text size="sm" weight="semibold">
                          API key or token
                        </Text>
                      </Flex>
                      <Text size="xs" className="text-muted-foreground">
                        Provide an API key or bearer token for authenticating API requests
                      </Text>
                    </Box>

                    <Box
                      className="p-4 bg-card border border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                      onClick={chooseApiBasic}
                    >
                      <Flex align="center" gap="sm" className="mb-1.5">
                        <Lock size={20} className="text-muted-foreground" />
                        <Text size="sm" weight="semibold">
                          API Basic Auth
                        </Text>
                      </Flex>
                      <Text size="xs" className="text-muted-foreground">
                        HTTP Basic Authentication with username:password sent on every request
                      </Text>
                    </Box>

                    <Box
                      className="p-4 bg-card border border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                      onClick={chooseApiLogin}
                    >
                      <Flex align="center" gap="sm" className="mb-1.5">
                        <PlugsConnected size={20} className="text-muted-foreground" />
                        <Text size="sm" weight="semibold">
                          API Login
                        </Text>
                      </Flex>
                      <Text size="xs" className="text-muted-foreground">
                        Authenticate via a login API endpoint to obtain a bearer token
                      </Text>
                    </Box>

                    {testSignInEnabled && (
                      <Box
                        className="p-4 bg-card border border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                        onClick={chooseTestSignIn}
                      >
                        <Flex align="center" gap="sm" className="mb-1.5">
                          <IdentificationCard size={20} className="text-muted-foreground" />
                          <Text size="sm" weight="semibold">
                            Test sign-in (Cofactor)
                          </Text>
                        </Flex>
                        <Text size="xs" className="text-muted-foreground">
                          The credential is a test user (email + name). Cofactor signs it in
                          deterministically through your app&apos;s real login.
                        </Text>
                      </Box>
                    )}

                    {errorMessage && (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                        <p className="text-sm text-red-500">{errorMessage}</p>
                      </div>
                    )}
                  </Flex>
                )}

                {showForm && (
                  <>
                    <CredentialForm
                      propertyId={propertyId}
                      credentialId={credentialId}
                      value={formData}
                      onChange={setFormData}
                      disabled={isLoading}
                      templateLocked={lockTemplate}
                      isMcpProperty={isMcpProperty}
                    />
                    {errorMessage && (
                      <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                        <p className="text-sm text-red-500">{errorMessage}</p>
                      </div>
                    )}
                  </>
                )}
              </DialogContent>
            </motion.div>
          )}

          {(phase === 'saving' ||
            phase === 'testing' ||
            phase === 'success' ||
            phase === 'failure') && (
            <motion.div
              key="testing"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              <DialogContent>
                <CredentialTestingView
                  phase={phase}
                  credentialName={formData.name || 'Credential'}
                  browserStreamState={browserStream.state}
                  errorMessage={errorMessage}
                  loginWorkflowCreated={browserStream.state.loginWorkflowCreated}
                  isApiLogin={formData.authProvider === 'api_login'}
                  onCancelTest={handleCancelTest}
                  onRetry={handleRetry}
                  onSkip={handleSkip}
                  onContinue={handleContinue}
                />
              </DialogContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Dialog>
    </DialogTrigger>
  );
}
